/** Service Desk console: spare-part requests (AST-092, SD-S4-06). */
export const serviceSpares = {
  title: "Spare parts",
  tab: "Spare parts",
  subtitle: "What engineers have asked the warehouse for, what is picked and what has been handed over.",
  progress: "Request progress",
  status: { requested: "Requested", picked: "Picked", issued: "Issued", cancelled: "Cancelled" },
  stepState: { done: "done", current: "current step", pending: "not yet" },
  actions: {
    create: "Request spare part",
    request: "Request spare part",
    pick: "Mark as picked",
    issue: "Mark as issued…",
    edit: "Edit",
    cancel: "Cancel request",
    send: "Send to stock system…",
    sendAgain: "Send to stock system again…",
  },
  filters: {
    group: "Filter by status",
    chip: { all: "All", requested: "Requested", picked: "Picked", issued: "Issued", cancelled: "Cancelled" },
    search: "Search by number, SKU or part name",
    searchLabel: "Search spare-part requests",
    mine: "Mine only",
    clear: "Clear filters",
  },
  list: {
    label: "Spare-part requests",
    loading: "Loading spare-part requests",
    number: "Request",
    partsHeader: "Parts",
    for: "For",
    status: "Status",
    neededBy: "Needed by",
    asked: "Asked",
    asset: "Asset",
    noLink: "No ticket or asset",
    open: "Open spare-part request {{number}}",
    parts_one: "{{count}} part in {{lines}} line",
    parts_other: "{{count}} parts in {{lines}} lines",
    more_one: "{{count}} more line",
    more_other: "{{count}} more lines",
    showing: "Showing {{shown}} of {{total}}",
    errorTitle: "Spare-part requests couldn't load",
    errorBody: "Check your connection and try again. Nothing was changed.",
  },
  empty: {
    title: "No spare-part requests yet",
    body: "When an engineer needs parts for a repair, the request shows up here for the warehouse to pick and issue.",
    filteredTitle: "No request matches",
    filteredBody: "Try another status or a shorter search.",
  },
  form: {
    title: "Request spare part",
    hint: "List each part once with the quantity you need. The warehouse picks and issues them.",
    editTitle: "Edit {{number}}",
    editHint: "A request can be changed until the warehouse picks it.",
    forContext: "For",
    lines: "Parts",
    sku: "SKU",
    name: "Part name",
    qty: "Qty",
    skuOf: "SKU of part {{number}}",
    nameOf: "Name of part {{number}}",
    qtyOf: "Quantity of part {{number}}",
    removeLine: "Remove part {{number}}",
    addLine: "Add a part",
    neededBy: "Needed by",
    neededByHint: "Optional. The day the part has to be with the engineer.",
    note: "Note for the warehouse",
    noteHint: "Optional. Not sent to a stock system.",
    submit: "Send request",
    save: "Save changes",
    created: "Request {{number}} sent to the warehouse",
    updated: "Request {{number}} updated",
    problems: {
      sku: "Enter the SKU.",
      duplicate: "This SKU is already in the request.",
      name: "Enter the part name.",
      qty: "Enter a whole number from 1 to {{max}}.",
      tooMany: "A request holds at most {{max}} parts.",
    },
  },
  drawer: {
    title: "Spare-part request {{number}}",
    titleLoading: "Spare-part request",
    loading: "Loading the request",
    actions: "Request actions",
    lines: "Parts",
    linesLabel: "Parts in this request",
    part: "Part",
    asked: "Asked",
    issued: "Issued",
    short: "Short",
    details: "Details",
    ticket: "Ticket",
    asset: "Asset",
    visit: "Visit",
    openAsset: "Open the asset",
    openVisit: "Open the visit",
    neededBy: "Needed by",
    requested: "Requested",
    quantity: "Quantity",
    parts_one: "{{count}} part in {{lines}} line",
    parts_other: "{{count}} parts in {{lines}} lines",
    pickedAt: "Picked {{at}}",
    issuedAt: "Issued {{at}}",
    cancelled: "This request was cancelled.",
    cancelledWhy: "This request was cancelled: {{reason}}",
  },
  pick: { done: "Request {{number}} marked as picked" },
  issue: {
    title: "Issue {{number}}",
    hint: "Enter what the warehouse handed over. Leave the quantities as they are to issue every line in full.",
    asked_one: "{{count}} asked",
    asked_other: "{{count}} asked",
    issued: "Issued",
    issuedOf: "Quantity issued for {{sku}}",
    problem: "Enter a whole number from 0 to {{max}}.",
    invalid: "Fix the quantities marked above. A line cannot be issued more than was asked.",
    submit: "Mark as issued",
    done: "Request {{number}} issued",
  },
  cancel: {
    title: "Cancel {{number}}?",
    hint: "The warehouse stops working on it. A cancelled request cannot be reopened.",
    reason: "Reason",
    reasonHint: "Optional. It stays on the request.",
    keep: "Keep request",
    submit: "Cancel request",
    done: "Request {{number}} cancelled",
  },
  send: {
    title: "Send {{number}} to the stock system?",
    body: "This writes the request to your purchasing system with the connector operation create_stock_request. The SKUs and quantities are sent; the note is not.",
    once: "A request is written once. If the system refuses it, you can send it again.",
    confirm: "Send to stock system",
    done: "Request {{number}} sent to the stock system",
  },
  external: {
    sent: "Sent to the purchasing system {{at}}.",
    reference: "Reference {{reference}}",
    failed: "The purchasing system did not accept this request (code {{code}}). You can send it again.",
    unavailable: "Sending to a stock system isn't set up: the commerce profile's connector has no published create_stock_request operation.",
  },
  context: {
    title: "Spare parts",
    none: "No spare parts requested for this ticket.",
    noneVisit: "No spare parts requested for this visit.",
    error: "Spare parts couldn't load.",
  },
  errors: {
    noStockOperation: "The commerce profile's connector has no published create_stock_request operation. Ask an admin to publish it.",
    alreadySent: "This request was already written to the purchasing system.",
    invalidTransition: "That step isn't available for this request any more. Reload to see where it is.",
    notFound: "This request or what it points at is no longer there.",
    plan: "Your plan doesn't include this.",
    forbidden: "You don't have permission to do that.",
  },
} as const;
